import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Save } from 'lucide-react';
import { Section } from '@/shared/ui/layout/Section';
import { Input } from '@/shared/ui/inputs/Input';
import { Select } from '@/shared/ui/selects/Select';
import { Button } from '@/shared/ui/buttons/Button';
import { useMutate } from '@/shared/hooks/useMutate';
import { usePerm } from '@/shared/hooks/usePerm';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiLimitConfigPut } from '@/entities/limit-config';
import {
  DEFAULT_TIMEZONE,
  getTimezoneOptions,
  limitConfigSchema,
  type LimitConfigFormValues,
} from '@/features/limit-config/modules/types';
import type {
  ApiLimitConfigGetResponse,
  ApiLimitConfigPutBody,
} from '@/shared/types/api/limit-config';

type Props = {
  config: ApiLimitConfigGetResponse;
};

const toFormValues = (config: ApiLimitConfigGetResponse): LimitConfigFormValues => ({
  amount: config.amount,
  period_unit: config.period_unit,
  period_value: config.period_value,
  start_day: config.start_day,
  start_date: config.start_date ?? '',
  timezone: config.timezone || DEFAULT_TIMEZONE,
  reset_usage: false,
});

export const FeaturesLimitConfigLimitConfigForm = ({ config }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const canEdit = usePerm('pricing.edit');

  const timezoneOptions = useMemo(
    () => getTimezoneOptions().map((tz) => ({ value: tz, label: tz })),
    [],
  );
  const periodUnitOptions = useMemo(
    () =>
      (['DAY', 'MONTH'] as const).map((value) => ({
        value,
        label: t(`limitConfig.form.periodUnit.${value}`),
      })),
    [t],
  );

  const form = useForm<LimitConfigFormValues>({
    resolver: zodResolver(limitConfigSchema),
    defaultValues: toFormValues(config),
  });

  useEffect(() => {
    form.reset(toFormValues(config));
  }, [config, form]);

  const periodUnit = form.watch('period_unit');

  const save = useMutate({
    api: (body: ApiLimitConfigPutBody) => apiLimitConfigPut(body),
    invalidateKeys: [queryKeys.limitConfig],
    onSuccess: () => {
      toast.success(t('limitConfig.toast.saved'));
      form.setValue('reset_usage', false);
    },
    onError: (_e, m) => toast.error(m),
  });

  const onSubmit = (v: LimitConfigFormValues): void => {
    const body: ApiLimitConfigPutBody = {
      amount: v.amount,
      period_unit: v.period_unit,
      period_value: v.period_value,
      start_day: v.start_day,
      start_date: v.period_unit === 'DAY' ? v.start_date || null : null,
      timezone: v.timezone,
      reset_usage: v.reset_usage,
    };
    save.mutate(body);
  };

  const amountError = form.formState.errors.amount?.message;
  const periodValueError = form.formState.errors.period_value?.message;
  const startDayError = form.formState.errors.start_day?.message;
  const timezoneError = form.formState.errors.timezone?.message;

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <Section title={t('limitConfig.form.amountSection')}>
        <Input
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          label={t('limitConfig.form.fields.amount')}
          required
          hint={t('limitConfig.form.hints.amount')}
          prefix="$"
          error={amountError ? t(amountError) : undefined}
          {...form.register('amount')}
        />
      </Section>

      <Section
        title={t('limitConfig.form.cadenceSection')}
        description={t('limitConfig.form.cadenceDescription')}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Controller
            control={form.control}
            name="period_unit"
            render={({ field }) => (
              <Select
                label={t('limitConfig.form.fields.periodUnit')}
                required
                name={field.name}
                options={periodUnitOptions}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
              />
            )}
          />
          <Input
            type="number"
            inputMode="numeric"
            min={1}
            step="1"
            label={
              periodUnit === 'DAY'
                ? t('limitConfig.form.fields.periodValueDay')
                : t('limitConfig.form.fields.periodValueMonth')
            }
            required
            error={periodValueError ? t(periodValueError) : undefined}
            {...form.register('period_value')}
          />
        </div>

        {periodUnit === 'MONTH' ? (
          <Input
            type="number"
            inputMode="numeric"
            min={1}
            max={31}
            step="1"
            label={t('limitConfig.form.fields.startDay')}
            required
            hint={t('limitConfig.form.hints.startDay')}
            error={startDayError ? t(startDayError) : undefined}
            {...form.register('start_day')}
          />
        ) : (
          <Input
            type="date"
            label={t('limitConfig.form.fields.startDate')}
            hint={t('limitConfig.form.hints.startDate')}
            {...form.register('start_date')}
          />
        )}

        <Controller
          control={form.control}
          name="timezone"
          render={({ field }) => (
            <Select
              label={t('limitConfig.form.fields.timezone')}
              required
              name={field.name}
              options={timezoneOptions}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              onBlur={field.onBlur}
              error={timezoneError ? t(timezoneError) : undefined}
            />
          )}
        />
      </Section>

      <Section title={t('limitConfig.form.resetSection')}>
        <Controller
          control={form.control}
          name="reset_usage"
          render={({ field }) => (
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                onBlur={field.onBlur}
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-slate-300 text-brand-600 focus:ring-2 focus:ring-brand-200"
              />
              <span className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-ink-soft">
                  {t('limitConfig.form.fields.resetUsage')}
                </span>
                <span className="text-xs text-ink-muted">
                  {field.value
                    ? t('limitConfig.form.hints.resetUsageOn')
                    : t('limitConfig.form.hints.resetUsageOff')}
                </span>
              </span>
            </label>
          )}
        />
      </Section>

      {canEdit && (
        <div className="flex justify-end">
          <Button
            type="submit"
            leftIcon={<Save className="h-4 w-4" />}
            loading={save.isPending}
          >
            {t('limitConfig.form.submit')}
          </Button>
        </div>
      )}
    </form>
  );
};
